import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { QuestionnaireValidation } from '../../types/questionnaire';
import Button from '../ui/Button';

interface QuestionnaireResultProps {
  validation: QuestionnaireValidation;
  onContinue?: () => void;
  onCancel?: () => void;
  serviceName?: string;
}

const QuestionnaireResult: React.FC<QuestionnaireResultProps> = ({
  validation,
  onContinue,
  onCancel,
  serviceName
}) => {
  const isEligible = validation.is_eligible;
  const hasContraindications = validation.contraindications && validation.contraindications.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-white rounded-2xl shadow-lg p-8">
        {/* Header avec icône de résultat */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
            isEligible ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {isEligible ? (
              <CheckCircle className="text-green-600" size={40} />
            ) : (
              <XCircle className="text-red-600" size={40} />
            )}
          </div>
          
          <h2 className="font-playfair text-3xl font-bold text-gray-800 mb-4">
            {isEligible ? 'Questionnaire validé !' : 'Contre-indications détectées'}
          </h2>
          
          <p className={`text-lg ${isEligible ? 'text-green-700' : 'text-red-700'}`}>
            {isEligible 
              ? `Vous êtes éligible pour le service ${serviceName || 'sélectionné'}.`
              : 'Des contre-indications ont été identifiées.'
            }
          </p>
        </div>

        {/* Message personnalisé */}
        {validation.message && (
          <div className={`rounded-xl p-6 mb-6 ${
            isEligible ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start space-x-3">
              <Info className={`flex-shrink-0 mt-0.5 ${isEligible ? 'text-green-600' : 'text-red-600'}`} size={20} />
              <p className={`${isEligible ? 'text-green-800' : 'text-red-800'}`}>
                {validation.message}
              </p>
            </div>
          </div>
        )}

        {/* Liste des contre-indications */}
        {hasContraindications && (
          <div className="mb-8">
            <h3 className="font-playfair font-bold text-gray-800 mb-4 flex items-center">
              <AlertTriangle className="text-orange-500 mr-2" size={20} />
              Contre-indications identifiées :
            </h3>
            
            <div className="space-y-3">
              {validation.contraindications?.map((contraindication, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start space-x-3 p-4 bg-orange-50 border border-orange-200 rounded-lg"
                >
                  <XCircle className="text-orange-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-orange-800 text-sm">{contraindication}</span>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Recommandations */}
        <div className={`rounded-xl p-6 mb-8 ${
          isEligible ? 'bg-blue-50 border border-blue-200' : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <h3 className={`font-playfair font-bold mb-3 ${
            isEligible ? 'text-blue-800' : 'text-yellow-800'
          }`}>
            {isEligible ? 'Prochaines étapes :' : 'Nos recommandations :'}
          </h3>
          
          {isEligible ? (
            <div className="space-y-2 text-blue-700 text-sm">
              <p>• Vous pouvez procéder à la réservation en toute sécurité</p>
              <p>• En cas de changement de votre état de santé, prévenez-nous</p>
            </div>
          ) : (
            <div className="space-y-2 text-yellow-700 text-sm">
              <p>• <strong>Consultez votre médecin</strong> avant de procéder au service</p>
              <p>• Obtenez un certificat médical de non contre-indication si nécessaire</p>
              <p>• Contactez-nous si vous avez des questions sur votre éligibilité</p>
              <p>• Vous pourrez repasser le questionnaire après consultation médicale</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          {onCancel && (
            <Button
              variant="secondary"
              onClick={onCancel}
              className="flex-1"
            >
              {isEligible ? 'Modifier mes réponses' : 'Retour'}
            </Button>
          )}
          
          {isEligible && onContinue && (
            <Button
              variant="primary"
              onClick={onContinue}
              className="flex-1"
            >
              Finaliser la réservation
            </Button>
          )}
          
          {!isEligible && (
            <Button
              variant="primary"
              onClick={() => window.open('tel:+33123456789', '_self')}
              className="flex-1"
            >
              Nous contacter
            </Button>
          )}
        </div>

        {/* Information de contact */}
        {!isEligible && (
          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm">
              Pour toute question, contactez-nous au{' '}
              <a href="tel:+33123456789" className="text-[#D4A574] font-semibold hover:underline">
                06 28 97 99 07
              </a>
              {' '}ou par email à{' '}
              <a href="mailto:contact@douxcemoman.fr" className="text-[#D4A574] font-semibold hover:underline">
                contact@douxcemoman.fr
              </a>
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default QuestionnaireResult;
